import { DEFAULT_NUMBER_OF_NIGHTS } from '@constants'
import { ISearchLocation } from '@typescript'
import { parseDateInClubTimeZone } from '@utils/dateUtils'
import { EMkDateFormat, formatDateForUI } from '@utils/formatDateForUI'

import { AvailabilityAction, AvailabilityActionType, AvailabilityState } from './types'

const DATE_ONLY_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

const isValidDateOnlyString = (dateValue: string): boolean => {
  const match = dateValue.match(DATE_ONLY_PATTERN)
  if (!match) return false

  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const day = parseInt(match[3], 10)
  const normalized = new Date(Date.UTC(year, month - 1, day))

  // Ensure real calendar validity (e.g. reject 2026-02-30).
  return normalized.getUTCFullYear() === year && normalized.getUTCMonth() === month - 1 && normalized.getUTCDate() === day
}

/**
 * Parses saved-search date strings into Date objects.
 *
 * Date-only values (`YYYY-MM-DD`) are interpreted as club-timezone calendar dates.
 * Timestamp values (ISO date-time strings) are parsed directly to preserve existing behavior.
 *
 * @param dateValue Saved-search date string.
 * @returns Parsed Date when valid, otherwise null.
 */
const parseSavedSearchDate = (dateValue?: string): Date | null => {
  if (!dateValue) return null

  if (isValidDateOnlyString(dateValue)) {
    return parseDateInClubTimeZone(dateValue)
  }

  const parsedDate = new Date(dateValue)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

/**
 * Reducer function to handle availability-related actions and update the state accordingly.
 *
 * @param state - The current state of availability.
 * @param action - The action dispatched to the reducer.
 * @returns The updated state based on the action type.
 *
 * The reducer handles the following action types:
 * - `AvailabilityActionType.SET_START_DATE`: Sets the start date and updates the check-in date.
 * - `AvailabilityActionType.SET_END_DATE`: Sets the end date and updates the check-out date.
 * - `AvailabilityActionType.SET_CURRENT_MONTH`: Sets the current month.
 * - `AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_SUCCESS`: Indicates successful fetch of check-in rate plan.
 * - `AvailabilityActionType.CHECKOUT_AVAIL_FETCH_SUCCESS`: Indicates successful fetch of check-out availability.
 * - `AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_FAILURE`: Indicates failure to fetch check-in rate plan and sets an error.
 * - `AvailabilityActionType.CHECKOUT_AVAIL_FETCH_FAILURE`: Indicates failure to fetch check-out availability and sets an error.
 * - `AvailabilityActionType.RESET_DATES`: Resets the dates to the initial state while preserving the current month.
 */
export const availabilityReducer = (state: AvailabilityState, action: AvailabilityAction): AvailabilityState => {
  switch (action.type) {
    case AvailabilityActionType.CHECKOUT_AVAIL_FETCH_SUCCESS:
    case AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
      }
    case AvailabilityActionType.CHECKOUT_AVAIL_FETCH_FAILURE:
    case AvailabilityActionType.CHECKIN_RATEPLAN_FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        availabilityError: action.payload,
      }
    case AvailabilityActionType.SET_LOCATION:
      return {
        ...state,
        location: action.payload,
      }
    case AvailabilityActionType.RESET_LOCATION:
      return {
        ...state,
        location: undefined,
      }
    case AvailabilityActionType.SET_BONUS_TIME_STATE:
      return {
        ...state,
        isBonusTimeEnabled: action.payload.isBonusTimeEnabled,
        isExoticBonusTimeEnabled: action.payload.isExoticBonusTimeEnabled,
      }
    case AvailabilityActionType.SET_CURRENT_MONTH:
      return {
        ...state,
        currentMonth: action.payload,
      }
    case AvailabilityActionType.SET_START_DATE:
      return {
        ...state,
        startDate: action.payload,
        checkInDate: action.payload ? formatDateForUI(action.payload, EMkDateFormat.ISO) : null,
      }
    case AvailabilityActionType.SET_END_DATE:
      return {
        ...state,
        endDate: action.payload,
        checkOutDate: action.payload ? formatDateForUI(action.payload, EMkDateFormat.ISO) : null,
      }
    case AvailabilityActionType.RESET_DATES: {
      const { currentMonth, isFlexibleDates, isBonusTimeDates } = state
      return {
        ...state,
        currentMonth,
        startDate: null,
        endDate: null,
        checkInDate: null,
        checkOutDate: null,
        isFlexibleDates,
        isBonusTimeDates,
        flexiDatesString: '',
        bonusTimeDatesString: '',
        monthYears: '',
        numberOfNights: DEFAULT_NUMBER_OF_NIGHTS,
        selectedFlexibleDateMonths: [],
      }
    }

    case AvailabilityActionType.RESET_FLEXIBLE_DATES: {
      return {
        ...state,
        isFlexibleDates: false,
        flexiDatesString: '',
        monthYears: '',
        numberOfNights: 0,
      }
    }

    case AvailabilityActionType.RESET_BONUS_TIME_DATES: {
      return {
        ...state,
        isBonusTimeDates: false,
      }
    }

    case AvailabilityActionType.SET_FLEXIBLE_DATES: {
      return {
        ...state,
        isFlexibleDates: action.payload,
      }
    }

     case AvailabilityActionType.SET_BONUS_TIME_DATES: {
      return {
        ...state,
        isBonusTimeDates: action.payload,
      }
    }

    case AvailabilityActionType.SET_DATES_BUFFER_ENABLED: {
      return {
        ...state,
        isDatesBufferEnabled: action.payload,
      }
    }
    case AvailabilityActionType.SET_SELECTED_FLEXIBLE_DATE_MONTHS: {
      return {
        ...state,
        selectedFlexibleDateMonths: action.payload,
      }
    }

    case AvailabilityActionType.SET_NUMBER_OF_NIGHTS:
      return {
        ...state,
        numberOfNights: action.payload,
      }
    case AvailabilityActionType.SET_MONTH_YEARS:
      return {
        ...state,
        monthYears: action.payload,
      }
    case AvailabilityActionType.SET_FLEXI_DATES_STRING:
      return {
        ...state,
        flexiDatesString: action.payload,
      }
    case AvailabilityActionType.SET_BONUS_TIME_DATES_STRING:
      return {
        ...state,
        bonusTimeDatesString: action.payload,
      }
    case AvailabilityActionType.SET_AVAILABLE_SUITES:
      return {
        ...state,
        suites: action.payload,
      }
    case AvailabilityActionType.SET_SELECTED_SUITE:
      return {
        ...state,
        selectedSuite: action.payload,
      }
    case AvailabilityActionType.SET_SELECTED_RESORT:
      return {
        ...state,
        selectedResort: action.payload,
      }
    case AvailabilityActionType.SET_SAVED_SEARCH: {
      const { searchLocation } = action.payload
      const location =
        typeof searchLocation === 'string'
          ? ({ title: searchLocation, locationPath: searchLocation, query: searchLocation } as ISearchLocation)
          : (searchLocation as ISearchLocation)

      const startDate = parseSavedSearchDate(action.payload.searchDateStart) ?? state.startDate
      const endDate = parseSavedSearchDate(action.payload.searchDateEnd) ?? state.endDate

      return {
        ...state,
        savedSearch: action.payload,
        isLoading: true,
        location: location || state.location,
        startDate,
        endDate,
        checkInDate: action.payload.searchDateStart || state.checkInDate,
        checkOutDate: action.payload.searchDateEnd || state.checkOutDate,
      }
    }
    case AvailabilityActionType.SET_LOADING_STATE:
      return {
        ...state,
        isLoading: action.payload,
      }
    case AvailabilityActionType.FETCH_SAVED_SEARCHES_SUCCESS:
      return {
        ...state,
        savedSearches: action.payload,
        savedSearchesError: undefined,
        isLoading: false,
      }
    case AvailabilityActionType.FETCH_SAVED_SEARCHES_FAILURE:
      return {
        ...state,
        savedSearchesError: action.payload,
        isLoading: false,
      }
    case AvailabilityActionType.SAVE_SEARCH_SUCCESS:
      return {
        ...state,
        saveSearchSuccess: true,
        saveSearchError: undefined,
        savedSearch: action.payload,
        isLoading: false,
      }
    case AvailabilityActionType.SAVE_SEARCH_FAILURE:
      return {
        ...state,
        saveSearchSuccess: false,
        saveSearchError: action.payload,
        isLoading: false,
      }
    case AvailabilityActionType.DELETE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        deleteSearchSuccess: true,
        deleteSearchError: undefined,
        isLoading: false,
        savedSearches: state.savedSearches.filter((search) => search.id.toString() !== action.payload),
      }
    case AvailabilityActionType.DELETE_SAVED_SEARCH_FAILURE:
      return {
        ...state,
        deleteSearchSuccess: false,
        deleteSearchError: action.payload,
        isLoading: false,
      }
    case AvailabilityActionType.SET_FILTERS:
      return {
        ...state,
        filters: action.payload,
      }
    case AvailabilityActionType.SET_SAVED_SEARCH_FILTERS_PAYLOAD:
      return {
        ...state,
        savedSearchFiltersPayload: action.payload,
      }
    case AvailabilityActionType.SET_IS_SAVED_SEARCH_EXECUTION:
      return {
        ...state,
        isSavedSearchExecution: action.payload,
      }
    case AvailabilityActionType.UPDATE_FILTERS_FROM_RESPONSE:
      return {
        ...state,
        filters: action.payload,
      }
    case AvailabilityActionType.SET_UNFILTERED_SEARCH_RESULTS:
      return {
        ...state,
        unfilteredSearchResults: action.payload,
      }
    case AvailabilityActionType.SET_SEARCH_CONSTRAINTS:
      return {
        ...state,
        searchConstraints: action.payload,
      }
    case AvailabilityActionType.SET_SEARCH_RESPONSE_META:
      return {
        ...state,
        searchAlertMessage: action.payload.alertMessage,
        searchBusinessSystemCode: action.payload.businessSystemCode,
      }
    default:
      return state
  }
}
